<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import { DgrLoading } from '@stockmarkteam/donguri-ui';
import { PromisePool } from '@supercharge/promise-pool';
import { CanceledError } from 'axios';
import dayjs from 'dayjs';
import path from 'path';
import AnewsStorageUploadProgressModal from '@/components/admin/user-documents/anews-storage-upload-progress-modal.vue';
import { useUserDocumentUsage } from '@/utils/swr';
import { checkMoveToAdmin } from '@/utils/user';
import {
  createZipFile,
  FILE_PART_SIZE,
  formatUsageFileSize,
  processFileEntry,
  range,
  readPartData,
} from '@/utils/user-document/file';

type FileValidationStatus =
  | 'UNCHECKED'
  | 'FILE_IS_NOT_SELECTED' // 今のUIだとファイル未選択の状態が来ることはないはずだが一応定義する
  | 'OK'
  | 'FILE_COUNT_INVALID'
  | 'FILE_SIZE_EXCEEDED'
  | 'CONTRACT_LIMIT_REACHED';

const MAX_FILE_COUNT = 3000;
const MAX_TOTAL_FILE_SIZE_BYTES = 10 * 1024 * 1024 * 1024;
const UPLOAD_CONCURRENCY_LIMIT = 10;
const MAX_RETRY_NUMBER = 2;

const FILE_SIZE_EXCEEDED_LABEL = formatUsageFileSize(MAX_TOTAL_FILE_SIZE_BYTES);

const props = defineProps<{ processing: boolean }>();
const emit = defineEmits<{
  uploadCallback: [];
}>();

const router = useRouter();
const { data: userDocumentUsage } = useUserDocumentUsage(true, false);
const isDragOver = ref(false);
const files = ref<File[]>([]);
const isProcessing = ref(props.processing);
const fileInputRef = ref<HTMLInputElement>();
const existFileNames = ref<Record<string, boolean>>({});

// ファイルアップロード時に使う
const zipCreateAbortController = ref(new AbortController());
const uploadController = ref(new AbortController());
const overflowFileCount = ref(false);
// 各パートのアップロード済みサイズ
const isProgressModalOpen = ref<boolean>(false);
const isUploadError = ref<boolean>(false);
const uploadTotal = ref<number>(0);
const uploadPartLoaded = ref<{ [index: number]: number }>({});
const uploadProgress = computed<number>(() => {
  const allLoaded = Object.values(uploadPartLoaded.value).reduce(
    (acc, cur) => acc + cur,
    0,
  );
  return Math.floor((allLoaded / uploadTotal.value) * 100) || 0;
});

const fileValidationStatus = ref<FileValidationStatus>('UNCHECKED');
const isShowDisplayErrorMessage = computed(() => {
  return (
    fileValidationStatus.value === 'FILE_IS_NOT_SELECTED' ||
    fileValidationStatus.value === 'FILE_COUNT_INVALID' ||
    fileValidationStatus.value === 'FILE_SIZE_EXCEEDED' ||
    fileValidationStatus.value === 'CONTRACT_LIMIT_REACHED'
  );
});
const displayErrorMessage = computed(() => {
  switch (fileValidationStatus.value) {
    case 'FILE_IS_NOT_SELECTED':
      return 'ファイルが未選択です';
    case 'FILE_COUNT_INVALID':
      return `アップロード上限数の${MAX_FILE_COUNT}ファイルを超えているためアップロードできません。ファイル数を減らして再度ファイルを選択してください。`;
    case 'FILE_SIZE_EXCEEDED':
      return `合計ファイルサイズが${FILE_SIZE_EXCEEDED_LABEL}を超えているためアップロードできません。ファイルサイズを減らして再度ファイルを選択してください。`;
    case 'CONTRACT_LIMIT_REACHED':
      return '全体ストレージ容量または全体ファイル追加・更新上限量を超えているためアップロードできません。';
    default:
      return '';
  }
});

// ファイル選択ボタンをおした時の挙動
// OSのファイル選択ダイアログを開く
const handleClick = () => fileInputRef.value?.click();

// ファイル選択した時のイベント
const handleFileInputChange = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files) {
    await executeFileProcessing(async () => {
      if (target.files && target.files.length > 0) {
        if (validateFileLength(Array.from(target.files))) {
          files.value.push(...Array.from(target.files));
        } else {
          files.value = [];
        }
      }
    });
  }
};

// ドラッグ＆ドラッグ＆ドロップ関連のイベント
const handleDragOver = () => (isDragOver.value = true);

const handleDragLeave = (event: DragEvent) =>
  (isDragOver.value = (event.currentTarget as Element)?.contains(
    event.relatedTarget as Node,
  ));

const handleDrop = async (event: DragEvent) => {
  isDragOver.value = false;

  if (event.dataTransfer?.items) {
    // DataTransferItemListはドラッグ操作中およびドロップイベント中しか有効でなく、イベントハンドラ修了後はデータが失われる。
    // そのため、イベントハンドラを抜ける前（非同期処理の前）にFileSystemEntryの配列を作成する。
    const entriesArray: FileSystemEntry[] = [];
    for (let i = 0; i < event.dataTransfer.items.length; i++) {
      const entry = event.dataTransfer?.items[i].webkitGetAsEntry();
      if (entry) {
        entriesArray.push(entry);
      }
    }
    await executeFileProcessing(async () => {
      /**
       * NOTE: ZIP化でフォルダ階層が失われるため、隠しフォルダ配下の除外は取り込みバッチだけでは保証できない。
       * FEでの除外は別途対応する。
       */
      for (const item of entriesArray) {
        const entryFiles = await processFileEntry(item);
        if (validateFileLength(entryFiles)) {
          files.value.push(...entryFiles);
        } else {
          files.value = [];
        }
      }
    });
  }
};

const validateFileLength = (addFiles: File[]) => {
  if (files.value.length + addFiles.length > MAX_FILE_COUNT) {
    overflowFileCount.value = true;
    return false;
  }
  overflowFileCount.value = false;
  return true;
};

const executeFileProcessing = async (processFiles: () => Promise<void>) => {
  if (isProcessing.value) return;

  isProcessing.value = true;
  try {
    await processFiles();

    // バリデーション. エラーがある場合は処理を止める
    if (!validUploadFiles()) {
      return;
    }

    const file_names = files.value.map((file: File) => file.name);
    existFileNames.value = await api.checkExistsAnewsStorageFiles(file_names);
    await uploadFile();
  } finally {
    isProcessing.value = false;
  }
};

const validUploadFiles = () => {
  // ステータスを UNCHECKED に戻す
  fileValidationStatus.value = 'UNCHECKED';

  if (overflowFileCount.value) {
    fileValidationStatus.value = 'FILE_COUNT_INVALID';
    return false;
  }

  if (files.value.length === 0) {
    fileValidationStatus.value = 'FILE_IS_NOT_SELECTED';
    return false;
  }

  if (files.value.length > MAX_FILE_COUNT) {
    fileValidationStatus.value = 'FILE_COUNT_INVALID';
    return false;
  }

  if (userDocumentUsage.value?.limit_reached) {
    fileValidationStatus.value = 'CONTRACT_LIMIT_REACHED';
    return false;
  }

  let totalSize = 0;
  for (const file of files.value) {
    totalSize += file.size;
    if (totalSize > MAX_TOTAL_FILE_SIZE_BYTES) {
      fileValidationStatus.value = 'FILE_SIZE_EXCEEDED';
      files.value = [];
      return false;
    }
  }

  fileValidationStatus.value = 'OK';
  return true;
};

// ファイルアップロード処理
const uploadFile = async () => {
  isUploadError.value = false;
  isProgressModalOpen.value = true;
  uploadController.value = new AbortController();
  uploadPartLoaded.value = {};
  zipCreateAbortController.value = new AbortController();

  try {
    const file = await createZipFile(
      makeUniqueFiles(),
      zipCreateAbortController.value.signal,
    );
    uploadTotal.value = file.size;

    // データソース作成
    await api.postTeamAnewsStorageDatasource();

    const partCount = Math.ceil(file.size / FILE_PART_SIZE);
    const fileParts = await Promise.all(
      range(0, partCount - 1).map(index => readPartData(file, index)),
    );

    // 進捗の初期化
    uploadPartLoaded.value = Array.from({ length: partCount }, () => 0);

    // 署名付きURL発行依頼
    const {
      urls,
      upload_id: uploadId,
      upload_file_key: uploadFileKey,
    } = await api.getAnewsStorageUploadUrls(partCount);
    // ファイルアップロード
    await uploadFileParts(urls, fileParts);
    // アップロード完了通知
    await api.postAnewsStorageUploadComplete(uploadFileKey, uploadId);
    emit('uploadCallback');
    isProgressModalOpen.value = false;
  } catch {
    isUploadError.value = true;
  }
};

// ファイルの名前が既に登録しているファイル名と
// 階層構造になっているフォルダで複数ファイルアップロードするときに名前が重複しないようにユニークな名前をつける
const makeUniqueFiles = () => {
  return files.value.map(file => {
    const existFileList = existFileNames.value;
    // 同じ名前のファイルは存在しないのでそのままのファイルを使う
    if (!existFileList[file.name]) {
      existFileNames.value[file.name] = true;
      return file;
    }
    // 既に同じ名前のファイルが存在しているので
    // 上書しないように今のファイルの名前のsuffixに `_現在日時_UUIDv4の先頭8文字`とする
    // 当初はユーザIDまで入れることを考えたが、ユーザIDをあまり表には出さない方がよいと思うので止めた
    // 同時に3,000件のファイルまでしかあげないので、UUID v4の先頭の8文字が衝突する確率は 0.0035%らしい(by gemini)
    const extension = path.extname(file.name);
    const basename = path.basename(file.name, extension);
    const uploadDateTime = dayjs().format('YYYYMMDD_HHmmss');
    const random = crypto.randomUUID().split('-')[0];
    const fileName = `${basename}_${uploadDateTime}_${random}${extension}`;
    existFileNames.value[fileName] = true;
    return new File([file], fileName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  });
};

const uploadFileParts = async (urls: string[], fileParts: Uint8Array[]) => {
  const onProcess = async (partData: Uint8Array, index: number) => {
    const onUploadProgress = (loaded: number) => {
      uploadPartLoaded.value[index] = loaded;
    };
    const uploadTask = async () => {
      await api.putAnewsStorageUpload(
        urls[index],
        partData,
        onUploadProgress,
        uploadController.value.signal,
      );
    };
    const shouldRetry = (e: unknown) => !(e instanceof CanceledError);
    await retryPromise(MAX_RETRY_NUMBER, uploadTask, shouldRetry);
  };

  const onError = (e: unknown) => {
    stopUpload();
    throw e;
  };
  await new PromisePool()
    .withConcurrency(UPLOAD_CONCURRENCY_LIMIT)
    .for(fileParts)
    .handleError(onError)
    .process(onProcess);
};

const retryPromise = async <T,>(
  maxRetryNumber: number,
  action: () => Promise<T>,
  shouldRetry: (e: unknown) => boolean = () => true,
): Promise<T | undefined> => {
  for (let retryCount = 0; retryCount <= maxRetryNumber; retryCount++) {
    try {
      return await action();
    } catch (e) {
      if (!shouldRetry(e) || retryCount >= maxRetryNumber) throw e;
    }
  }
};

const stopUpload = async () => {
  zipCreateAbortController.value.abort();
  uploadController.value.abort();

  // 各値をリセットする
  files.value = [];
  existFileNames.value = {};
  uploadTotal.value = 0;
  uploadPartLoaded.value = {};
  fileValidationStatus.value = 'UNCHECKED';
};

onMounted(async () => {
  await checkMoveToAdmin(router);
});

// unmount時にはアップロードを止める
onUnmounted(async () => {
  await stopUpload();
});

// ステータスの変更を監視してアップロード中か判断する
watch(
  () => props.processing,
  newVal => {
    isProcessing.value = newVal;
  },
  { immediate: true },
);

// モーダルが閉じられたらアップロードを中止
watch(isProgressModalOpen, async newValue => {
  if (!newValue) {
    await stopUpload();
  }
});
</script>

<template>
  <div
    class="container"
    :class="{ 'drag-over': isDragOver }"
    v-if="!isProcessing"
  >
    <input
      ref="fileInputRef"
      type="file"
      multiple
      style="display: none"
      @change="handleFileInputChange"
    />
    <div
      class="drag-drop-area"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <div class="upload-content">
        <div class="c-text-regular--m">
          ファイルやフォルダをここにドラッグ&amp;ドロップ
        </div>
        <div class="c-text-regular--s limit-text upload-button">
          1度にアップロード可能なファイル数は最大{{ MAX_FILE_COUNT }}件です
          <button
            class="c-outlineBtn c-outlineBtn--auto c-outlineBtn--secondary"
            @click="handleClick"
          >
            ファイルを選択
          </button>
        </div>
        <div
          class="c-text-regular--m error-message"
          v-if="isShowDisplayErrorMessage"
        >
          {{ displayErrorMessage }}
        </div>
      </div>
    </div>
  </div>
  <div class="processing" v-else>
    <div class="c-text-regular--m loading">
      <div>
        <DgrLoading size="small" />
      </div>
      <div>同期処理中</div>
    </div>
    <div class="c-text-regular--s">
      現在追加でアップロードすることはできません。完了するまでお待ちください。
    </div>
  </div>
  <AnewsStorageUploadProgressModal
    v-model:is-open="isProgressModalOpen"
    :progress="uploadProgress"
    :error-text="
      isUploadError
        ? 'アップロードに失敗しました。しばらくしてからやり直してください。'
        : undefined
    "
  />
</template>

<style scoped lang="scss">
.container {
  background-color: $color-white;
  border: 1px dashed $color-gray400;
  border-radius: 4px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  justify-content: center;

  .drag-drop-area {
    width: 100%;
    padding: 16px 0;
  }

  .drag-over {
    border: solid 1px $color-gray1000;
  }

  .upload-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .limit-text {
    color: $color-gray800;
  }

  .upload-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .error-message {
    color: $color-orange1000;
  }
}

.processing {
  background-color: $color-gray400;
  text-align: center;
  border-radius: 4px;
  padding: 16px 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  justify-content: center;

  .loading {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }
}

.dialog-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.dialog-radio {
  margin-top: 16px;
}
</style>
